/**
 * Renders a tree view of sites and groups.
 * @param {Object} props - The component props.
 * @param {Array} props.orgSiteGroups - The array of site and group data.
 * @param {Function} props.onorgSiteGroupSearch - The callback function for searching sites and groups.
 * @returns {JSX.Element} The rendered component.
 */
import React, { useEffect, useState } from "react";
import {
  Box,
  Grow,
  Collapse,
  Fade,
  Checkbox,
  CircularProgress,
  Tooltip,
} from "@mui/material";
import { StyledIconButton } from "../../../../components/StyledComponents/Buttons/AuradineButtons.js";
import {
  BodyLarge,
  BodySmall,
  LabelSmall,
} from "../../../../components/StyledComponents/Typography/Typography.tsx";
import ArrowDropDownIcon from "@mui/icons-material/ArrowDropDown";
import ArrowDropUpIcon from "@mui/icons-material/ArrowDropUp";
import Close from "@mui/icons-material/Close";
import { StyledCheckbox } from "../../../../components/StyledComponents/Inputs/Inputs.js";
import { check } from "prettier";
import useMinerPageContext from "../../../context/useMinerPageContext.js";
import useOrgSiteGroupTreeContext from "../../../context/useOrgSiteGroupTreeContext.js";
import { useLocation } from "react-router-dom/cjs/react-router-dom.js";
import { useHistory } from "react-router-dom";
import { LeftSidePanel } from "../LeftPanel.js";
import { useTheme } from "../../../../ThemeContext/ThemeContext.js";

/**The SiteAndGroupTreeItem component takes in several props:
site: Represents the current org.
setSiteSelected: A function to update the selected org.
siteSelected: Represents the currently selected org.
setSiteSelectedName: A function to update the name of the selected org.
setGroupSelectedName: A function to update the name of the selected group.
siteSelectedName: Represents the name of the currently selected org.
groupSelectedName: Represents the name of the currently selected group. */

//Inside the component, there is a state variable called treeOpen initialized with a value of false. This state variable will be used to control the visibility of the tree.
const OrgSiteGroupTreeItem = (props) => {
  const {
    org,
    expandOrg,
    expandSite,
    setSiteSelected,
    siteSelected,
    orgSelected,
    groupSelected,
    setGroupSelected,
    setOrgSelected,
    setSiteSelectedName,
    setGroupSelectedName,
    setOrgSelectedName,
    orgSelectedName,
    siteSelectedName,
    groupSelectedName,
    multiSelectModel,
    setOrgSiteGroups,
    orgSiteGroups,
    setNumberOfSelections,
    numberOfSelections,
    sidePanelMode,
  } = props;
  const [orgTreeOpen, setOrgTreeOpen] = React.useState(false);
  const [siteTreeOpen, setSiteTreeOpen] = React.useState({});
  let allGroupSelectedInSite = false;
  const location = useLocation();
  const history = useHistory();
  const { colors } = useTheme();
  const onOrgSelectedMatriceChange = (e) => {
    if (e.target.checked) {
      const orgCount = 1;
      const siteCount = org.siteList.reduce((acc, curr) => {
        return acc + 1;
      }, 0);
      if (org.siteList.some((site) => site.selectedSite)) {
        const siteCount = org.siteList.reduce((acc, curr) => {
          return !curr.selectedSite ? acc + 1 : acc;
        }, 0);
        setNumberOfSelections((prev) => prev + orgCount + siteCount);
      } else {
        setNumberOfSelections((prev) => prev + orgCount + siteCount);
      }
      const orgModifications = {
        ...org,
        selectedOrg: e.target.checked,
        siteList: org.siteList.map((site) => ({
          ...site,
          selectedSite: true,
        })),
      };
      const orgSiteGroupInfo = [...orgSiteGroups];
      const indexToReplaceAt = orgSiteGroupInfo.findIndex(
        (orgSiteGroup) => org.org_name === orgSiteGroup.org_name
      );
      orgSiteGroupInfo.splice(indexToReplaceAt, 1, orgModifications);
      setOrgSiteGroups(orgSiteGroupInfo);
    } else {
      const orgModifications = {
        ...org,
        selectedOrg: e.target.checked,
        siteList: org.siteList.map((site) => ({
          ...site,
          selectedSite: false,
        })),
      };
      const orgCount = 1;
      const siteCount = org.siteList.reduce((acc, curr) => {
        return acc + 1;
      }, 0);
      setNumberOfSelections((prev) =>
        prev - orgCount - siteCount <= 0 ? 0 : prev - orgCount - siteCount
      );
      const orgSiteGroupInfo = [...orgSiteGroups];
      const indexToReplaceAt = orgSiteGroupInfo.findIndex(
        (orgSiteGroup) => org.org_name === orgSiteGroup.org_name
      );
      orgSiteGroupInfo.splice(indexToReplaceAt, 1, orgModifications);
      setOrgSiteGroups(orgSiteGroupInfo);
    }
  };

  const checkAllSitesSelected = () => {
    return (
      org.siteList.length > 0 &&
      org.siteList?.every((site) => site.selectedSite)
    );
  };

  const checkAllGroupsDeselected = () => {
    return (
      org.siteList.length > 0 &&
      org.siteList?.every((site) => !site.selectedSite)
    );
  };

  const onSiteSelectedMatriceChange = (e, index) => {
    if (e.target.checked) {
      console.log("in here");
      const orgModifications = {
        ...org,
        siteList: org.siteList.map((site, i) =>
          i === index ? { ...site, selectedSite: e.target.checked } : site
        ),
      };

      if (orgModifications.siteList.every((site) => site.selectedSite)) {
        console.log("");
        orgModifications.selectedOrg = true;
        setNumberOfSelections((prev) => prev + 2);
      } else {
        setNumberOfSelections((prev) => prev + 1);
      }

      const orgSiteGroupInfo = [...orgSiteGroups];
      const indexToReplaceAt = orgSiteGroupInfo.findIndex(
        (orgSiteGroup) => org.org_name === orgSiteGroup.org_name
      );
      orgSiteGroupInfo.splice(indexToReplaceAt, 1, orgModifications);
      setOrgSiteGroups(orgSiteGroupInfo);
    } else {
      const orgModifications = {
        ...org,
        siteList: org.siteList.map((site, i) =>
          i === index ? { ...site, selectedSite: e.target.checked } : site
        ),
      };

      if (
        orgModifications.siteList.some(
          (site) => !site.selectedSite && orgModifications.selectedSite
        )
      ) {
        orgModifications.selectedOrg = false;
        setNumberOfSelections((prev) => prev - 2);
      } else {
        setNumberOfSelections((prev) => prev - 1);
      }
      const orgSiteGroupInfo = [...orgSiteGroups];
      const indexToReplaceAt = orgSiteGroupInfo.findIndex(
        (orgSiteGroup) => org.org_name === orgSiteGroup.org_name
      );
      orgSiteGroupInfo.splice(indexToReplaceAt, 1, orgModifications);
      setOrgSiteGroups(orgSiteGroupInfo);
    }
  };

  const checkAtleastOneSiteSelected = () => {
    return (
      org.siteList.length > 0 && org.siteList?.some((site) => site.selectedSite)
    );
  };

  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    let siteName = params.get("siteName");
    let dgname = params.get("dgname");
    let org_name = params.get("org_name");

    if (!org_name && !siteName && !dgname) {
      const siteGroupCS =
        JSON.parse(sessionStorage.getItem("siteGroupCS")) || {};
      siteName = siteGroupCS.siteName;
      dgname = siteGroupCS.dgname;
      org_name = siteGroupCS.org_name;
    }

    if (siteName) {
      setSiteSelectedName(siteName);
      if (!dgname && !org_name) {
        setSiteSelected(true);
        setOrgSelected(false);
        setGroupSelected(false);
      }
    }

    if (dgname) {
      setGroupSelectedName(dgname);
      if (!siteName && !org_name) {
        setSiteSelected(false);
        setOrgSelected(false);
        setGroupSelected(true);
      }
    }

    if (org_name) {
      setOrgSelectedName(org_name);
      if (!dgname && !siteName) {
        setSiteSelected(false);
        setOrgSelected(true);
        setGroupSelected(false);
      }
    }
  }, []);

  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    let siteName = params.get("siteName");

    if (expandOrg) {
      setOrgTreeOpen(true);
    }

    if (expandSite) {
      const newSiteTreeOpen = {};
      org.siteList.forEach((site) => {
        if (site.siteName === siteName) {
          newSiteTreeOpen[site.siteName] = true;
        }
      });
      setSiteTreeOpen((prev) => ({
        ...prev,
        ...newSiteTreeOpen,
      }));
    }
  }, [expandOrg, expandSite])

  return (
    <Box
      display={"flex"}
      flexDirection={"column"}
      borderBottom={`1px solid ${colors.border.card}`}
    >
      <Box
        className={"org_information"}
        display={"flex"}
        flexDirection={"row"}
        justifyContent={"space-between"}
        alignItems={"center"}
        padding={" 0.5rem 1.5rem 0.5rem 1rem"}
        onClick={() => {
          console.log("org clicked");
          const url = new URL(window.location.href);
          url.searchParams.set("org_name", org?.org_name);
          url.searchParams.delete("siteName");
          url.searchParams.delete("dgname");
          window.history.replaceState({}, "", url);
          setOrgSelected(true);
          setSiteSelected(false);
          setGroupSelected(false);
          setOrgSelectedName(org?.org_name);
          setSiteSelectedName("");
          setGroupSelectedName("");
          const orgSiteGroup = {
            org_name: org?.org_name,
            siteName: "",
            dgname: "",
          };
          sessionStorage.setItem("siteGroupCS", JSON.stringify(orgSiteGroup));
        }}
        gap={{ xs: 1, sm: 2, md: 4 }}
        sx={{
          cursor: "pointer",
          transition: "all 0.3s ease-in-out",
          backgroundColor:
            orgSelectedName === org?.org_name && !!orgSelected ? colors.primary.light  : "",
          borderRadius: "6.25rem",
          "&:hover": {
            backgroundColor:
              orgSelectedName === org?.org_name && !!orgSelected
                ? colors.primary.light
                : colors.hover.muted,
            borderRadius: "6.25rem",
          },
        }}
      >
        <Box
          display={"flex"}
          flexDirection={"row"}
          justifyContent={"space-between"}
          flexGrow={1}
          alignItems={"center"}
        >
          {!siteSelected &&
            !groupSelected &&
            orgSelectedName === org?.org_name &&
            !!orgSelected && (
              <StyledIconButton 
                colors={colors}
                onClick={(event) => {
                  event.stopPropagation();
                  setSiteSelectedName("");
                  setOrgSelectedName("");
                  setOrgSelected(false);
                  setSiteSelected(false);
                  setGroupSelected(false);
                  setGroupSelectedName("");
                  const url = new URL(window.location.href);
                  url.searchParams.delete("org_name");
                  url.searchParams.delete("siteName");
                  url.searchParams.delete("dgname");
                  window.history.replaceState({}, "", url);
                  const orgSiteGroup = {
                    org_name: "",
                    siteName: "",
                    dgname: "",
                  };
                  sessionStorage.setItem(
                    "siteGroupCS",
                    JSON.stringify(orgSiteGroup)
                  );
                }}
              >
                <Close fontSize="small" />
              </StyledIconButton>
            )}
          <Box
            display={"flex"}
            flexDirection={"row"}
            alignItems={"center"}
            flexShrink={1}
            gap={2}
          >
            {multiSelectModel && (
              <StyledCheckbox
                colors={colors}
                indeterminate={
                  checkAtleastOneSiteSelected() && !checkAllSitesSelected()
                }
                checked={org.selectedOrg || checkAllSitesSelected()}
                onChange={onOrgSelectedMatriceChange}
              />
            )}
            <Tooltip title={org?.org_name}>
              <Box>
                <BodyLarge
                  noWrap
                  sx={
                    sidePanelMode
                      ? {
                          overflow: "hidden",
                          textOverflow: "ellipsis",
                          whiteSpace: "nowrap",
                          maxWidth: {
                            xs: "3rem",
                            sm: "4rem",
                            md: "8rem",
                            lg: "10rem",
                            xl: "12rem",
                          }, // ⬅️ Adjust based on layout
                        }
                      : {}
                  }
                >
                  {org?.org_name}
                </BodyLarge>
              </Box>
            </Tooltip>
          </Box>
          {(() => {
            let orgDeviceCount = 0;
            if (org?.siteList.length > 0) {
              org?.siteList.forEach((site) => {
                if (site.groups && site.groups.length > 0) {
                  site.groups.forEach((group) => {
                    orgDeviceCount += group.deviceCount;
                  });
                }
              });
            }
            return <LabelSmall>{orgDeviceCount}</LabelSmall>;
          })()}
        </Box>
        <Box
          display={"flex"}
          flexDirection={"row"}
          justifyContent={"flex-end"}
          alignItems={"center"}
        >
          <StyledIconButton 
            colors={colors}
            onClick={(event) => {
              event.stopPropagation();
              setOrgTreeOpen((prev) => !prev);
            }}
            disabled={org?.siteList && org?.siteList?.length === 0}
          >
            {orgTreeOpen ? (
              <>
                <ArrowDropUpIcon />
              </>
            ) : (
              <>
                <ArrowDropDownIcon />
              </>
            )}
          </StyledIconButton>
        </Box>
      </Box>
      {orgTreeOpen &&
        org?.siteList &&
        org?.siteList?.length > 0 &&
        org.siteList.map((sites, index) => {
          return (
            <>
              <Fade in={orgTreeOpen} key={index}>
                <Box
                  display={"flex"}
                  flexDirection={"row"}
                  justifyContent={"space-between"}
                  alignItems={"center"}
                  padding={"0.5rem 1.5rem 0.5rem 1rem"}
                  gap={{ xs: 1, sm: 2, md: 4 }}
                  onClick={() => {
                    setOrgSelected(false);
                    setSiteSelected(true);
                    setGroupSelected(false);
                    setSiteSelectedName(sites?.siteName);
                    setOrgSelectedName(org?.org_name);
                    setGroupSelectedName("");
                    const url = new URL(window.location.href);
                    url.searchParams.set("org_name", org?.org_name);
                    url.searchParams.set("siteName", sites?.siteName);
                    url.searchParams.delete("dgname");
                    window.history.replaceState({}, "", url);
                    const orgSiteGroup = {
                      org_name: org?.org_name,
                      siteName: sites?.siteName,
                      dgname: "",
                    };
                    sessionStorage.setItem(
                      "siteGroupCS",
                      JSON.stringify(orgSiteGroup)
                    );
                  }}
                  sx={{
                    cursor: "pointer",
                    transition: "all 0.3s ease-in-out",
                    borderRadius: "6.25rem",
                    backgroundColor: (
                      expandSite
                        ? siteSelectedName === sites?.siteName &&
                          siteSelected &&
                          !groupSelected
                        : siteSelectedName === sites?.siteName
                    )
                      ? colors.primary.light
                      : "",
                    "&:hover": {
                      backgroundColor: (
                        expandSite
                          ? siteSelectedName === sites?.siteName &&
                            siteSelected &&
                            !groupSelected
                          : siteSelectedName === sites?.siteName
                      )
                        ? colors.primary.light
                        : colors.hover.muted,
                      borderRadius: "6.25rem",
                    },
                  }}
                >
                  <Box
                    display={"flex"}
                    flexDirection={"row"}
                    justifyContent={"space-between"}
                    flexGrow={1}
                    alignItems={"center"}
                  >
                    {siteSelectedName === sites?.siteName &&
                      !groupSelectedName && (
                        <StyledIconButton 
                          colors={colors}
                          onClick={(event) => {
                            event.stopPropagation();
                            setSiteSelectedName("");
                            setOrgSelectedName("");
                            setOrgSelected(false);
                            setSiteSelected(false);
                            setGroupSelected(false);
                            setGroupSelectedName("");
                            const url = new URL(window.location.href);
                            url.searchParams.delete("siteName");
                            url.searchParams.delete("org_name");
                            url.searchParams.delete("dgname");
                            window.history.replaceState({}, "", url);
                            const orgSiteGroup = {
                              org_name: "",
                              siteName: "",
                              dgname: "",
                            };
                            sessionStorage.setItem(
                              "siteGroupCS",
                              JSON.stringify(orgSiteGroup)
                            );
                          }}
                        >
                          <Close fontSize="small" />
                        </StyledIconButton>
                      )}
                    <Box
                      display={"flex"}
                      flexDirection={"row"}
                      alignItems={"center"}
                      flexShrink={1}
                      gap={2}
                    >
                      <Box width={multiSelectModel ? 50 : 10}></Box>

                      {multiSelectModel && (
                        <StyledCheckbox
                          checked={org.siteList[index].selectedSite}
                          colors={colors}
                          onChange={(e) =>
                            onSiteSelectedMatriceChange(e, index)
                          }
                        />
                      )}
                      <Tooltip title={sites?.siteLocation ? `${sites?.siteName} - ${sites?.siteLocation}` : sites?.siteName}>
                        <Box>
                          <BodyLarge
                            noWrap
                            sx={
                              sidePanelMode
                                ? {
                                    overflow: "hidden",
                                    textOverflow: "ellipsis",
                                    whiteSpace: "nowrap",
                                    maxWidth: {
                                      xs: "3rem",
                                      sm: "4rem",
                                      md: "8rem",
                                      lg: "10rem",
                                      xl: "12rem",
                                    }, // ⬅️ Adjust based on layout
                                  }
                                : {}
                            }
                          >
                            {sites?.siteName}
                          </BodyLarge>
                          {sites?.siteLocation && (
                            <BodySmall
                              noWrap
                              sx={{
                                color: colors.text.hint,
                                fontSize: "0.75rem",
                                overflow: "hidden",
                                textOverflow: "ellipsis",
                                whiteSpace: "nowrap",
                                maxWidth: {
                                  xs: "3rem",
                                  sm: "4rem",
                                  md: "8rem",
                                  lg: "10rem",
                                  xl: "12rem",
                                },
                              }}
                            >
                              {sites?.siteLocation}
                            </BodySmall>
                          )}
                        </Box>
                      </Tooltip>
                    </Box>
                    {(() => {
                      let siteDeviceCount = 0;
                      if (sites?.groups?.length > 0) {
                        sites.groups.forEach((group) => {
                          siteDeviceCount += group.deviceCount;
                        });
                      }
                      return <LabelSmall>{siteDeviceCount}</LabelSmall>;
                    })()}
                  </Box>

                  <StyledIconButton 
                    colors={colors}
                    onClick={(e) => {
                      e.stopPropagation();
                      setSiteTreeOpen((prev) => ({
                        ...prev,
                        [sites.siteName]: !prev[sites.siteName],
                      }));
                    }}
                    disabled={!sites.groups || sites.groups.length === 0}
                  >
                    {siteTreeOpen?.[sites.siteName] ? (
                      <ArrowDropUpIcon />
                    ) : (
                      <ArrowDropDownIcon />
                    )}
                  </StyledIconButton>
                </Box>
              </Fade>
              {siteTreeOpen?.[sites.siteName] &&
                sites.groups?.map((group, groupIndex) => (
                  <Fade in={true} key={groupIndex}>
                    <Box
                      display="flex"
                      flexDirection="row"
                      justifyContent="space-between"
                      alignItems="center"
                      padding="0.5rem 1.5rem 0.5rem 1rem"
                      margin="0rem 1rem 0rem 1rem"
                      gap={{ xs: 1, sm: 2, md: 4 }}
                      sx={{
                        cursor: "pointer",
                        transition: "all 0.3s ease-in-out",
                        borderRadius: "6.25rem",
                        backgroundColor:
                          groupSelectedName === group.DGName &&
                          siteSelectedName === sites.siteName &&
                          orgSelectedName === org.org_name ? colors.primary.light : "",   
                        "&:hover": {
                          backgroundColor:
                            groupSelectedName === group.DGName &&
                            siteSelectedName === sites.siteName &&
                            orgSelectedName === org.org_name
                              ? colors.primary.light
                              : colors.hover.muted,
                        },
                      }}
                      onClick={() => {
                        setOrgSelected(false);
                        setSiteSelected(false);
                        setGroupSelected(true);
                        setGroupSelectedName(group.DGName);
                        setSiteSelectedName(sites.siteName);
                        setOrgSelectedName(org.org_name);
                        const url = new URL(window.location.href);
                        url.searchParams.set("org_name", org.org_name);
                        url.searchParams.set("siteName", sites.siteName);
                        url.searchParams.set("dgname", group.DGName);
                        window.history.replaceState({}, "", url);
                        sessionStorage.setItem(
                          "siteGroupCS",
                          JSON.stringify({
                            org_name: org.org_name,
                            siteName: sites.siteName,
                            dgname: group.DGName,
                          })
                        );
                      }}
                    >
                      <Box
                        display={"flex"}
                        flexDirection={"row"}
                        justifyContent={"space-between"}
                        flexGrow={1}
                        alignItems={"center"}
                      >
                        {groupSelectedName === group.DGName &&
                            siteSelectedName === sites.siteName &&
                            orgSelectedName === org.org_name && (
                          <StyledIconButton 
                            colors={colors}
                            onClick={(event) => {
                              event.stopPropagation();
                              setSiteSelectedName("");
                              setOrgSelectedName("");
                              setOrgSelected(false);
                              setSiteSelected(false);
                              setGroupSelected(false);
                              setGroupSelectedName("");
                              const url = new URL(window.location.href);
                              url.searchParams.delete("siteName");
                              url.searchParams.delete("org_name");
                              url.searchParams.delete("dgname");
                              window.history.replaceState({}, "", url);
                              const orgSiteGroup = {
                                org_name: "",
                                siteName: "",
                                dgname: "",
                              };
                              sessionStorage.setItem(
                                "siteGroupCS",
                                JSON.stringify(orgSiteGroup)
                              );
                            }}
                          >
                            <Close fontSize="small" />
                          </StyledIconButton>
                        )}
                        <Tooltip title={group.DGName}>
                          <Box padding={"0rem 1.5rem 0rem 1rem"}>
                            <BodyLarge
                              noWrap
                              sx={
                                sidePanelMode
                                  ? {
                                      overflow: "hidden",
                                      textOverflow: "ellipsis",
                                      whiteSpace: "nowrap",
                                      maxWidth: {
                                        xs: "3rem",
                                        sm: "4rem",
                                        md: "8rem",
                                        lg: "10rem",
                                        xl: "12rem",
                                      }, // ⬅️ Adjust based on layout
                                    }
                                  : {}
                              }
                            >
                              {group.DGName}
                            </BodyLarge>
                          </Box>
                        </Tooltip>
                        <LabelSmall>{group.deviceCount}</LabelSmall>
                      </Box>
                    </Box>
                  </Fade>
                ))}
            </>
          );
        })}
    </Box>
  );
};

//A  group tree is a hierarchical representation of sites and groups from our groups and sites management system.
const OrgSiteGroupTree = (props) => {
  const { colors } = useTheme();
  const {
    orgSiteGroups,
    setOrgSiteGroups,
    onorgSiteGroupSearch,
    clickDisabled,
    multiSelectModel,
    maxHeight,
    setNumberOfSelections,
    numberOfSelections,
    isSiteGroupDataFetching,
    isOrgSiteGroupDataLoading,
    isOrgSiteGroupDataFetched,
    sidePanelMode,
    searchValue,
  } = props;
  const [orgTreeOpen, setOrgTreeOpen] = React.useState(false);
  const [siteTreeOpen, setSiteTreeOpen] = React.useState(false);
  const [siteSelected, setSiteSelected] = useState(false);
  const [orgSelected, setOrgSelected] = useState(false);
  const [groupSelected, setGroupSelected] = useState(false);
  const {
    setSiteSelectedName,
    setGroupSelectedName,
    siteSelectedName,
    groupSelectedName,
    setOrgSelectedName,
    orgSelectedName,
  } = useOrgSiteGroupTreeContext();

  if (orgSiteGroups) {
    orgSiteGroups
      .sort((a, b) => a.org_name.localeCompare(b.org_name))
      .forEach((org) => {
        org.siteList
          .sort((a, b) => a.siteName.localeCompare(b.siteName))
          .forEach((site) => {
            site.groups.sort((a, b) => a.DGName.localeCompare(b.DGName));
          });
      });
  }

  return (
    <Box
      display={"flex"}
      flexDirection={"column"}
      flexGrow={1}
      overflow={"auto"}
      maxHeight={maxHeight ? maxHeight : "calc(100vh - 23rem)"}
    >
      {isOrgSiteGroupDataFetched &&
        orgSiteGroups &&
        orgSiteGroups.length > 0 &&
        orgSiteGroups?.map((org, index) => (
          <OrgSiteGroupTreeItem
            key={index}
            org={org}
            orgTreeOpen={orgTreeOpen}
            setOrgTreeOpen={setOrgTreeOpen}
            siteTreeOpen={siteTreeOpen}
            setSiteTreeOpen={setSiteTreeOpen}
            expandOrg={org.org_name === orgSelectedName && !!siteSelectedName}
            expandSite={!!groupSelectedName}
            orgSelected={orgSelected}
            setOrgSelected={setOrgSelected}
            siteSelected={siteSelected}
            setSiteSelected={setSiteSelected}
            groupSelected={groupSelected}
            setGroupSelected={setGroupSelected}
            siteSelectedName={siteSelectedName}
            setSiteSelectedName={setSiteSelectedName}
            groupSelectedName={groupSelectedName}
            setGroupSelectedName={setGroupSelectedName}
            orgSelectedName={orgSelectedName}
            setOrgSelectedName={setOrgSelectedName}
            clickDisabled={clickDisabled}
            multiSelectModel={multiSelectModel}
            setOrgSiteGroups={setOrgSiteGroups}
            orgSiteGroups={orgSiteGroups}
            setNumberOfSelections={setNumberOfSelections}
            numberOfSelections={numberOfSelections}
            sidePanelMode={sidePanelMode}
          />
        ))}

      {}

      {isOrgSiteGroupDataLoading && (
        <Box
          display={"flex"}
          flexDirection={"row"}
          justifyContent={"center"}
          alignItems={"center"}
          flexGrow={1}
        >
          {" "}
          <CircularProgress
            size={"2rem"}
            sx={{
              color: colors.primary.main,
            }}
          />
        </Box>
      )}

      {/* push this out of siteGroup */}
      {isOrgSiteGroupDataFetched &&
        orgSiteGroups &&
        orgSiteGroups.length === 0 &&
        searchValue !== "" && (
          <Box
            display={"flex"}
            justifyContent={"center"}
            alignItems={"center"}
            flexGrow={1}
          >
            <BodyLarge>No search results found</BodyLarge>
          </Box>
        )}
    </Box>
  );
};

export default OrgSiteGroupTree;
